/**
 * Microsoft Clarity integration — production-only, async, single-init.
 *
 * Loads the official Clarity tracking snippet exactly once per page load.
 * Exposes `trackEvent(name, payload?)` which:
 *   1) Fires the Clarity custom event so it shows up in clarity.microsoft.com
 *   2) Mirrors it to our backend (`/api/analytics/event`) so the Super Admin
 *      analytics page has its own counters without needing Clarity's API.
 *
 * Privacy: Clarity auto-masks <input type="password"> and any element with
 * the data-clarity-mask attribute. Sensitive forms (OTP, payment) set
 * data-clarity-mask="true" on the wrapper.
 *
 * Delegated tracking: `installDelegatedTracking()` installs ONE document-level
 * click listener that maps data-testid attributes to Clarity events. This
 * avoids editing dozens of CTA components.
 */

const PROD_HOSTS = new Set([
  'actanonedu.com',
  'www.actanonedu.com',
]);

const PROJECT_ID = (process.env.REACT_APP_CLARITY_PROJECT_ID || '').trim();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

let _initialised = false;
let _delegationInstalled = false;

function isProduction() {
  if (typeof window === 'undefined') return false;
  return PROD_HOSTS.has(window.location.hostname);
}

/** Initialise Clarity exactly once. Safe no-op on non-prod and on missing ID.
 *  Iter 76+: detects the head-tag injection in index.html and skips
 *  re-injection; still installs delegated click tracking and event mirroring. */
export function initClarity() {
  if (_initialised) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.navigator && window.navigator.webdriver) return;

  // Detect head-tag load (window.clarity is a function once the official
  // snippet has run — even before the remote script.js finishes downloading).
  const headLoaded = typeof window.clarity === 'function';

  if (!headLoaded) {
    // Fallback path: inject only on production and when Project ID is set.
    if (!PROJECT_ID) return;
    if (!isProduction()) return;
    (function (c, l, a, r, i) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      const t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      const y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', PROJECT_ID);
  }

  _initialised = true;
  installDelegatedTracking();
}

// ─── Delegated click-to-event mapping ────────────────────────────────────
// Maps data-testid (prefix or exact) → Clarity event name.
// Keep this list short and meaningful — one event per *intent*, not per button instance.
const TESTID_EVENT_MAP = [
  // Marketing / landing
  [/^hero-cta|get-started-btn|cta-get-started$/, 'hero_cta_click'],
  [/^cta-try-free|try-free|try-it-free|start-free|signup-cta-free/, 'try_free_click'],
  [/^cta-pricing|see-pricing|view-pricing|pricing-link|pricing-cta/, 'pricing_click'],
  [/^cta-book-demo|book-demo|demo-cta|schedule-demo/, 'book_demo_click'],
  [/^cta-whatsapp|whatsapp-cta|wa-contact|whatsapp-link/, 'whatsapp_click'],
  // Blog
  [/^blog-card-/, 'blog_post_viewed'],
  [/^blog-bookmark-btn$/, 'blog_post_bookmarked'],
  [/^blog-rating-(helpful|not-helpful)$/, 'blog_post_rated'],
  // Newsletter (submit handled separately for success-only)
];

export function installDelegatedTracking() {
  if (_delegationInstalled) return;
  if (typeof document === 'undefined') return;

  document.addEventListener('click', (ev) => {
    try {
      // Walk up from event.target to find the nearest element with data-testid
      let el = ev.target;
      let hops = 0;
      while (el && el !== document && hops < 6) {
        const tid = el.getAttribute && el.getAttribute('data-testid');
        if (tid) {
          for (const [pattern, event] of TESTID_EVENT_MAP) {
            if (pattern.test(tid)) {
              trackEvent(event, { testid: tid });
              return;
            }
          }
        }
        el = el.parentNode;
        hops += 1;
      }
    } catch (_e) { /* silent */ }
  }, { capture: true, passive: true });

  _delegationInstalled = true;
}

/** Fire a Clarity custom event AND log it to our backend for the SA dashboard. */
export function trackEvent(name, payload = {}) {
  if (!name) return;
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity('event', name);
    }
  } catch (_e) { /* silent */ }
  try {
    if (API_URL) {
      const body = JSON.stringify({
        event: name,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        ts: Date.now(),
        ...payload,
      });
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/api/site-analytics/event`, blob);
      } else {
        fetch(`${API_URL}/api/site-analytics/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    }
  } catch (_e) { /* silent */ }
}

/** Tag the current Clarity session with a user identifier (no PII!). */
export function identifyUser(userIdHash) {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function' && userIdHash) {
      window.clarity('identify', userIdHash);
    }
  } catch (_e) { /* silent */ }
}

/** Mark the current Clarity session as important (e.g., paid users). */
export function upgradeSession(reason) {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity('upgrade', reason);
    }
  } catch (_e) { /* silent */ }
}

export const CLARITY_PROJECT_ID = PROJECT_ID;
